import { useEffect, useMemo, useState } from "react";
import { Box, BoxProps, InternalStandardProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { RecommendationsDialog } from "./RecommendationsDialog";
import {
    useFilteredRecommendations,
    useGlobalRecommendations,
    useRecommendations,
} from "JS/React/Hooks/Recommendations";
import { useAudios, useGlobalAudios } from "JS/React/Hooks/Audio";
import {
    Content,
    EventActions,
    EventNames,
    Recommendations as IRecommendations,
} from "JS/Models";
import { fetchBatchThumbnailUrls } from "JS/React/Hooks/Common/ImageUpload";
import { useNavigate, useLocation } from "react-router-dom";
import qs from "qs";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { keyBy } from "lodash-es";
import { messaging } from "JS/Helpers/UserMessaging";
import { config } from "JS/Config";
import {
    resetRedirectionState,
    setConsentFormStats,
    setLoadingState,
} from "JS/Redux/ConsentForm";
import { useAppDispatch, useAppSelector } from "JS/Redux/Store";
import { useGetConsentStatusFromCMLM } from "JS/React/Hooks/ConsentForm";
import { RedirectionDialog } from "JS/React/Components/RedirectionDialog/RedirectionDialog";
import { getConsentStatusById } from "JS/Database/ConsentForm";
import { openSSOLink } from "JS/Helpers/HomePageHelper";
import {
    getIsLtdConsentFormOpened,
    setIsRedirectedToAmmwayConsentForm,
    setIsRedirectedToBundleOptInPage,
    setIsRedirectedToBundleSubsPage,
    setIsRedirectedToPurchaseBundlePage,
} from "JS/Helpers/LocalStorageHelpers";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import {
    setBundleOptInLoadingState,
    setBundleOptInPopupState,
} from "JS/Redux/BundleOptIn";
import {
    setBundleSubscriptionLoadingState,
    setBundleSubscriptionPopupState,
} from "JS/Redux/BundleSubscription";
import {
    setLoadingPurchaseBundle,
    setPurchaseBundlePopupState,
} from "JS/Redux/PurchaseBundle";
import {
    useGlobalMediaEssentials,
    useMediaEssentials,
} from "JS/React/Hooks/MediaEssentials";

export interface RecommendationsProps extends InternalStandardProps<BoxProps> {}
export type RecommendationQParams = {
    auto: boolean;
    push: boolean;
};

const StyledRoot = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
}));

export const Recommendations = (props: RecommendationsProps) => {
    const { className, ...rest } = props;
    const { localAuto, push } = useQueryParams();
    const dispatch = useAppDispatch();
    const { mediaEssentials } = useGlobalMediaEssentials();

    const { showPurchaseBundlePopup, loadingPurchaseBundle } = useAppSelector(
        (state) => state.purchaseBundle,
    );

    const {
        showBundleOptInPopup,
        ssoRedirectionPlace: bundleOptInSsoLink,
        loadingBundleOptIn,
    } = useAppSelector((state) => state.bundleOptIn);

    const {
        showBundleSubscriptionPopup,
        ssoRedirectionPlace: bundleSubsSsoLink,
        loadingBundleSubscription,
    } = useAppSelector((state) => state.bundleSubscription);

    const {
        recommendations,
        recommendedAudios,
        loading,
        onPurchaseAudio,
        loadingMessage,
        showRecommendations,
        audioError,
    } = useAudioRecommendations(push);
    const { refetch: fetchMediaEssentials } = useMediaEssentials();

    const { thumbnails } = useThumbnails(recommendedAudios);

    const { loading: consentStatusLoading } = useGetConsentStatusFromCMLM();

    const { showRedirectionDialog, loadingConsent } = useAppSelector(
        (state) => state.consentForm,
    );
    const { logFirebaseEvent } = useFirebaseLogger();

    const handleClose = () => {
        getConsentStatusById(config.user.memberId)
            .then((consentData) => {
                if (consentData?.redirectToAmwayConsentForm) {
                    openSSOLink(consentData.ssoRedirectionPlace, true);
                    setIsRedirectedToAmmwayConsentForm("true");

                    dispatch(
                        setConsentFormStats({
                            showRedirectionDialog: false,
                            loadingConsent: true,
                        }),
                    );

                    setTimeout(
                        () =>
                            dispatch(
                                setLoadingState({ loadingConsent: false }),
                            ),
                        3000,
                    );

                    logFirebaseEvent(EventNames.CONSENT_FORM_V2_REDIRECT, {
                        action: EventActions.CONSENT_FORM_V2_REDIRECT,
                    });
                }
            })
            .catch(() => dispatch(resetRedirectionState()));
    };

    const handleClosePurchaseBundlePopup = async () => {
        setIsRedirectedToPurchaseBundlePage("true");

        dispatch(
            setPurchaseBundlePopupState({
                showPurchaseBundlePopup: false,
                loadingPurchaseBundle: true,
            }),
        );
        const updatedMediaEssentialsResp = !mediaEssentials
            ?.user_subscriptions_for_purchase_bundle?.length
            ? mediaEssentials
            : await fetchMediaEssentials();

        setTimeout(
            () =>
                dispatch(
                    setLoadingPurchaseBundle({
                        loadingPurchaseBundle: false,
                    }),
                ),
            3000,
        );

        logFirebaseEvent(EventNames.PURCHASE_BUNDLE_REDIRECT, {
            action: EventActions.PURCHASE_BUNDLE_REDIRECT,
        });
        openSSOLink(
            updatedMediaEssentialsResp.purchase_bundle_sso_redirection_place,
            true,
        );
    };

    const handleCloseBundleOptInPopup = () => {
        setIsRedirectedToBundleOptInPage("true");

        dispatch(
            setBundleOptInPopupState({
                showBundleOptInPopup: false,
                loadingBundleOptIn: true,
            }),
        );

        setTimeout(
            () =>
                dispatch(
                    setBundleOptInLoadingState({
                        loadingBundleOptIn: false,
                    }),
                ),
            3000,
        );

        logFirebaseEvent(EventNames.BUNDLE_OPT_IN_REDIRECT, {
            action: EventActions.BUNDLE_OPT_IN_REDIRECT,
        });
        openSSOLink(bundleOptInSsoLink, true);
    };

    const handleCloseBundleSubscriptionPopup = () => {
        setIsRedirectedToBundleSubsPage("true");

        dispatch(
            setBundleSubscriptionPopupState({
                showBundleSubscriptionPopup: false,
                loadingBundleSubscription: true,
            }),
        );

        setTimeout(
            () =>
                dispatch(
                    setBundleSubscriptionLoadingState({
                        loadingBundleSubscription: false,
                    }),
                ),
            3000,
        );

        logFirebaseEvent(EventNames.BUNDLE_SUBSCRIPTION_REDIRECT, {
            action: EventActions.BUNDLE_SUBSCRIPTION_REDIRECT,
        });
        openSSOLink(bundleSubsSsoLink, true);
    };

    return (
        <StyledRoot className={className} {...rest}>
            {loading && (
                <AppBackdropProgress
                    open={loading}
                    backdropText={loadingMessage}
                />
            )}
            {consentStatusLoading && (
                <AppBackdropProgress
                    open={consentStatusLoading}
                    backdropText={loadingMessage}
                />
            )}
            {showRecommendations &&
                !audioError &&
                !(
                    showBundleOptInPopup ||
                    showBundleSubscriptionPopup ||
                    showPurchaseBundlePopup
                ) &&
                !(
                    loadingBundleOptIn ||
                    loadingBundleSubscription ||
                    loadingPurchaseBundle
                ) && (
                    <RecommendationsDialog
                        recommendedAudios={recommendedAudios}
                        recommendations={recommendations}
                        thumbnails={thumbnails}
                        isAuto={localAuto}
                        push={push}
                        onPurchaseAudio={onPurchaseAudio}
                    />
                )}
            {(loadingConsent ||
                loadingBundleOptIn ||
                loadingBundleSubscription ||
                loadingPurchaseBundle) && (
                <AppBackdropProgress
                    open={loadingConsent}
                    backdropText={messaging?.loader?.loadingSession}
                />
            )}
            {showPurchaseBundlePopup &&
                !showRedirectionDialog &&
                !loadingConsent &&
                !consentStatusLoading &&
                getIsLtdConsentFormOpened() !== "true" && (
                    <RedirectionDialog
                        open={showPurchaseBundlePopup}
                        onClose={handleClosePurchaseBundlePopup}
                        dialogMessage={messaging.purchaseBundle.message}
                    />
                )}
            {showBundleOptInPopup &&
                !showRedirectionDialog &&
                !loadingConsent &&
                !consentStatusLoading &&
                getIsLtdConsentFormOpened() !== "true" && (
                    <RedirectionDialog
                        open={showBundleOptInPopup}
                        onClose={handleCloseBundleOptInPopup}
                        dialogMessage={messaging.bundleOptIn.popup.message}
                    />
                )}
            {showBundleSubscriptionPopup &&
                !showRedirectionDialog &&
                !loadingConsent &&
                !consentStatusLoading &&
                getIsLtdConsentFormOpened() !== "true" && (
                    <RedirectionDialog
                        open={showBundleSubscriptionPopup}
                        onClose={handleCloseBundleSubscriptionPopup}
                        dialogMessage={
                            messaging.bundleSubscription.popup.message
                        }
                    />
                )}
            {showRedirectionDialog && (
                <RedirectionDialog
                    open={showRedirectionDialog}
                    onClose={handleClose}
                    dialogMessage={messaging.consentForm.ssoRedirection}
                />
            )}
        </StyledRoot>
    );
};

const useAudioRecommendations = (push: boolean) => {
    const navigate = useNavigate();

    const { audios, setGlobalAudios } = useGlobalAudios();
    const { recommendationState } = useGlobalRecommendations();

    const {
        loading: audiosLoading,
        loaded: audiosLoaded,
        errorMsg,
    } = useAudios();
    const { recommendations, loading: recommendationsLoading } =
        useRecommendations(
            recommendationState?.recommendations != null && !push,
        );
    const { filteredRecommendedAudios, filteredRecommendations } =
        useFilteredRecommendations(recommendations, audios);

    const { sortedRecAudios } = useRecSorting(
        filteredRecommendedAudios,
        filteredRecommendations,
    );

    const onPurchaseAudio = (nid: string) => {
        const toSaveAudios: Content[] = audios.map((d) => {
            if (d.nid === nid) {
                return {
                    ...d,
                    is_purchased: true,
                    publish_free: "true",
                    content_price: 0.0,
                };
            } else {
                return d;
            }
        });
        setGlobalAudios(toSaveAudios);
    };

    useEffect(() => {
        if (errorMsg) {
            navigate(-1);
        }
    }, [errorMsg]);

    return {
        showRecommendations:
            !!recommendations &&
            (!!audios?.length || audiosLoaded) &&
            !recommendationsLoading,
        audioError: errorMsg,
        recommendedAudios: sortedRecAudios,
        recommendations: filteredRecommendations,
        loading: audiosLoading || recommendationsLoading,
        loadingMessage: audiosLoading
            ? messaging.loader.audios
            : messaging.loader.recommendations,
        onPurchaseAudio,
    };
};

const useRecSorting = (
    recAudios: Content[],
    recommendations: IRecommendations[],
) => {
    const sortedRecAudios = useMemo(() => {
        const recMap = recommendations
            ? keyBy(recommendations, (x) => `${x.nid}`)
            : {};

        return [...recAudios]?.sort(
            (a, b) => recMap[a.nid]?.rank - recMap[b.nid]?.rank,
        );
    }, [recAudios, recommendations]);

    return {
        sortedRecAudios,
    };
};

const useQueryParams = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const queryParams: RecommendationQParams = useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        const { auto = "false", push = "false" } = parsedQueryString;

        return {
            auto: auto == "true",
            push: push == "true",
        };
    }, [location?.search]);

    const setQueryParams = (params: Partial<RecommendationQParams>) => {
        navigate(
            {
                pathname: location.pathname,
                search: qs.stringify({ ...queryParams, ...params }),
            },
            { replace: true },
        );
    };

    const [localIsAuto] = useState(queryParams.auto);

    useEffect(() => {
        setQueryParams({ auto: false });
    }, []);

    return {
        localAuto: localIsAuto,
        push: queryParams.push,
    };
};

const useThumbnails = (recommendations: Content[]) => {
    const [thumbnails, setThumbnails] = useState<{
        [key: string]: string;
    }>(null);

    const fetchThumbnails = async () => {
        const itemsToFetch = recommendations
            .filter((d) => !thumbnails || !thumbnails[d.nid])
            .map((d) => ({
                nid: d.nid,
                postfix: d.image_url_postfix,
                fileName: d.image_file_name,
            }));

        if (itemsToFetch.length === 0) return;

        const newThumbnails = await fetchBatchThumbnailUrls(itemsToFetch);

        setThumbnails((prev) => ({
            ...prev,
            ...newThumbnails,
        }));
    };

    useEffect(() => {
        if (recommendations) {
            fetchThumbnails();
        }
    }, [recommendations]);

    return {
        thumbnails,
    };
};
